import {hasValue} from '@famly/stat_ts-utils_has-value';
import {type Failable, resolve, fail} from 'web-app/util/failable';

import type * as Local from './plan-types';

export type TimeRange = {start: string; end: string};
type SessionWithAvailabilityIndex = {
    flexibleHours: boolean;
    availability: {[day: string]: TimeRange};
};
export type SessionIndex = {[sessionId: string]: SessionWithAvailabilityIndex};

/**
 * Adds start or end times to session bookings in the children that need them.
 *
 * Session bookings for sessions with flexible hours require start and end
 * times. This means that even if `hasMissingSessionBookingTime`, it might be
 * tht session bookings do not get patched as the flexible hours flag can
 * only be determined when the sessions are available.
 *
 * @param children The children to patch.
 * @param sessions The sessions with session availabilities.
 * @return The patched children.
 */
export const patchMissingSessionBookingTimes = (
    children: Local.Child[],
    sessions: SessionIndex,
): Failable<Local.Child[], string> => {
    let errors: string[] = [];
    const logError = (msg: string) => {
        errors = [...errors, msg];
    };
    const patchedChildren = patchChildren(children, sessions, logError);
    if (errors.length > 0) {
        return fail(`Failed to patch session bookings in children: ${errors.join(' ')}`);
    }
    return resolve(patchedChildren);
};

const patchChildren = (
    children: Local.Child[],
    sessions: SessionIndex,
    logError: (msg: string) => void,
): Local.Child[] =>
    children.map(child => ({
        ...child,
        plans: child.plans.map(plan => ({
            ...plan,
            planParts: plan.planParts.map(part => ({
                ...part,
                sessionBookings: part.sessionBookings.map(patchSessionBooking(sessions, logError)),
            })),
        })),
    }));

const patchSessionBooking =
    (sessions: SessionIndex, logError: (msg: string) => void) =>
    (sessionBooking: Local.SessionBooking): Local.SessionBooking => {
        const session = sessions[sessionBooking.sessionId];
        if (!hasValue(session)) {
            logError(`No session with id ${sessionBooking.sessionId}`);
            return sessionBooking;
        }
        // Sessions without flexible hours do not need patching.
        if (!session.flexibleHours) {
            return sessionBooking;
        }
        const timeRange = session.availability[sessionBooking.day];
        if (!hasValue(timeRange)) {
            logError(`No session availability on ${sessionBooking.day}`);
            return sessionBooking;
        }
        return {
            ...sessionBooking,
            start: sessionBooking.start ?? timeRange.start,
            end: sessionBooking.end ?? timeRange.end,
        };
    };
